import {
  Box,
  Button,
  Container,
  Flex,
  FormControl,
  FormHelperText,
  FormLabel,
  Grid,
  Input,
  NumberInput,
  NumberInputField,
  Select,
  Text,
} from "@chakra-ui/react";
import AdminDataTable from "components/AdminDataTable/AdminDataTable";
import { useState } from "react";
import { queryClient } from "App";
import { convertParamsToString } from "helpers/stringManipulation";
import { patchRequest, postRequest, useMutationWrapper, useQueryWrapper } from "services/apiHelper";
import { categoryRequest } from "services/request";
import { queryKeys } from "services/querykeys";
import { type CategoryGroup, type CategoryType } from "utils/constants";
import { successToast } from "utils/hooks";

const emptyForm = {
  name: "",
  group: "Others" as CategoryGroup | "",
  sortOrder: "500",
};

// Stable row identity for the shared table.
const categoryRowId = (category: CategoryType): string => category.id;

const groupOptions: Array<{ label: string; value: CategoryGroup | "" }> = [
  { label: "No group", value: "" },
  { label: "Mass Parts", value: "Mass Parts" },
  { label: "Seasons", value: "Seasons" },
  { label: "Others", value: "Others" },
];

const getCategoryPayload = (formData: typeof emptyForm) => {
  const sortOrder = Number(formData.sortOrder);

  return {
    name: formData.name.trim(),
    group: formData.group === "" ? null : formData.group,
    ...(Number.isNaN(sortOrder) ? {} : { sortOrder }),
  };
};

const CategoryCatalogue = () => {
  const [formData, setFormData] = useState(emptyForm);
  const [editingCategoryId, setEditingCategoryId] = useState<string | null>(null);
  const [migrationResult, setMigrationResult] = useState<{
    updatedCount: number;
    unmatched: string[];
  } | null>(null);
  const { data, isLoading } = useQueryWrapper(queryKeys.CATEGORIES, categoryRequest.CATEGORIES, {
    staleTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const resetForm = () => {
    setFormData(emptyForm);
    setEditingCategoryId(null);
  };

  const onSaveSuccess = () => {
    void queryClient.invalidateQueries(queryKeys.CATEGORIES);
    successToast(editingCategoryId ? "Category updated" : "Category created");
    resetForm();
  };

  const onDisableSuccess = () => {
    void queryClient.invalidateQueries(queryKeys.CATEGORIES);
    successToast("Category disabled");
  };

  const onMigrateSuccess = (response) => {
    setMigrationResult(response?.data ?? null);
    successToast("Category migration completed");
  };

  const { mutate: createCategory, isLoading: isCreating } = useMutationWrapper(
    postRequest,
    onSaveSuccess,
  );
  const { mutate: updateCategory, isLoading: isUpdating } = useMutationWrapper(
    patchRequest,
    onSaveSuccess,
  );
  const { mutate: disableCategory, isLoading: isDisabling } = useMutationWrapper(
    patchRequest,
    onDisableSuccess,
  );
  const { mutate: migrateCategories, isLoading: isMigrating } = useMutationWrapper(
    postRequest,
    onMigrateSuccess,
  );

  const categories: CategoryType[] = data?.data ?? [];

  const handleChange = (event) => {
    setFormData({
      ...formData,
      [event.target.name]: event.target.value,
    });
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const payload = getCategoryPayload(formData);

    if (editingCategoryId) {
      updateCategory({
        url: convertParamsToString(categoryRequest.CATEGORY, { id: editingCategoryId }),
        data: payload,
      });
      return;
    }

    createCategory({
      url: categoryRequest.CATEGORIES,
      data: payload,
    });
  };

  const handleEdit = (category: CategoryType) => {
    setEditingCategoryId(category.id);
    setFormData({
      name: category.name,
      group: category.group ?? "",
      sortOrder: String(category.sortOrder ?? 500),
    });
  };

  const handleDisable = (category: CategoryType) => {
    const shouldDisable = window.confirm(`Disable ${category.name}?`);
    if (!shouldDisable) return;

    disableCategory({
      url: convertParamsToString(categoryRequest.CATEGORY, { id: category.id }),
      data: { active: false },
    });
  };

  const COLUMNS = [
    { Header: "Name", accessor: "name" },
    {
      Header: "Group",
      Cell: ({ cell }: any) => <Text>{cell.row.original?.group ?? "-"}</Text>,
    },
    { Header: "Slug", accessor: "slug" },
    { Header: "Sort", accessor: "sortOrder", align: "right" as const, minWidth: 70 },
    {
      Header: "Actions",
      align: "right" as const,
      minWidth: 180,
      Cell: ({ cell }: any) => (
        <>
          <Button
            size="sm"
            mr="2"
            onClick={() => {
              handleEdit(cell.row.original);
            }}
          >
            Edit
          </Button>
          <Button
            size="sm"
            variant="secondary"
            isLoading={isDisabling}
            onClick={() => {
              handleDisable(cell.row.original);
            }}
          >
            Disable
          </Button>
        </>
      ),
    },
  ];

  return (
    <Container mt="4" maxW="container.xl">
      <Flex
        alignItems={{ base: "stretch", md: "center" }}
        justifyContent="space-between"
        flexDir={{ base: "column", md: "row" }}
        gap="3"
        mb="4"
      >
        <Box>
          <Text as="h1" fontSize="1.3rem" color="#4e6e97" textTransform="uppercase">
            Categories
          </Text>
          <Text fontSize="sm" color="#5f6f7f">
            Active catalogue rows are shown here. Disabled categories stay attached to existing
            scripts but are hidden from selectors.
          </Text>
        </Box>
        <Flex gap="2">
          {editingCategoryId && (
            <Button variant="secondaryOutline" onClick={resetForm}>
              Cancel edit
            </Button>
          )}
          <Button
            variant="secondaryOutline"
            onClick={() => {
              migrateCategories({ url: categoryRequest.MIGRATE, data: {} });
            }}
            isLoading={isMigrating}
          >
            Run migration
          </Button>
        </Flex>
      </Flex>

      <Box borderWidth="1px" borderColor="#dbe3e9" p="4" mb="6">
        <form onSubmit={handleSubmit}>
          <Grid templateColumns={{ base: "repeat(1, 1fr)", lg: "2fr 1fr 1fr" }} gap="4">
            <FormControl isRequired>
              <FormLabel>Name</FormLabel>
              <Input name="name" value={formData.name} onChange={handleChange} />
            </FormControl>
            <FormControl>
              <FormLabel>Group</FormLabel>
              <Select name="group" value={formData.group ?? ""} onChange={handleChange}>
                {groupOptions.map((group) => (
                  <option key={group.label} value={group.value ?? ""}>
                    {group.label}
                  </option>
                ))}
              </Select>
              <FormHelperText m={0}>Groups only affect frontend rendering.</FormHelperText>
            </FormControl>
            <FormControl>
              <FormLabel>Sort order</FormLabel>
              <NumberInput
                value={formData.sortOrder}
                min={0}
                onChange={(value) => {
                  setFormData({ ...formData, sortOrder: value });
                }}
              >
                <NumberInputField name="sortOrder" />
              </NumberInput>
            </FormControl>
          </Grid>
          <Button mt="4" type="submit" isLoading={isCreating || isUpdating}>
            {editingCategoryId ? "Update category" : "Create category"}
          </Button>
        </form>
      </Box>

      {migrationResult && (
        <Box borderWidth="1px" borderColor="#dbe3e9" p="4" mb="6">
          <Text fontWeight="bold">Migration result</Text>
          <Text fontSize="sm" color="#5f6f7f">
            Updated scripts: {migrationResult.updatedCount}
          </Text>
          <Text mt="2" fontSize="sm" color="#5f6f7f">
            Unmatched values:{" "}
            {migrationResult.unmatched.length > 0 ? migrationResult.unmatched.join(", ") : "None"}
          </Text>
        </Box>
      )}

      <AdminDataTable
        ariaLabel="Categories"
        columns={COLUMNS}
        data={categories}
        emptyState={
          <Text color="#7f94a6" my="4">
            No active categories found.
          </Text>
        }
        getRowId={categoryRowId}
        loading={isLoading}
      />
    </Container>
  );
};

export default CategoryCatalogue;
